import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LandingPage from "./pages/LandingPage";
import DemoPage from "./pages/DemoPage";
import CareersPage from "./pages/CareersPage";
import ApplicationFlow from "./pages/ApplicationFlow";
import LoginPage from "./pages/LoginPage";
import SignOfferPage from "./pages/SignOfferPage";
import ScheduleInterviewPage from "./pages/ScheduleInterviewPage";
import OnboardingPage from "./pages/OnboardingPage";
import DashboardLayout from "./layouts/DashboardLayout";
import DashboardHome from "./pages/dashboard/DashboardHome";
import CandidatesPage from "./pages/dashboard/CandidatesPage";
import CandidateDetailPage from "./pages/dashboard/CandidateDetailPage";
import EmployeesPage from "./pages/dashboard/EmployeesPage";
import JobListingsPage from "./pages/dashboard/JobListingsPage";
import SettingsPage from "./pages/dashboard/SettingsPage";
import NotificationsPage from "./pages/dashboard/NotificationsPage";
import AnalyticsPage from "./pages/dashboard/AnalyticsPage";
import InterviewCalendarPage from "./pages/dashboard/InterviewCalendarPage";
import MessagesPage from "./pages/dashboard/MessagesPage";
import PrivacyPolicyPage from "./pages/Public/PrivacyPolicyPage";
import TermsAndConditionsPage from "./pages/Public/TermsAndConditionsPage";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#4B2D6E] border-t-transparent mx-auto mb-4"></div>
          <p className="text-[#666666]">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/apply/:jobId" element={<ApplicationFlow />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/sign-offer/:signingToken" element={<SignOfferPage />} />
          <Route path="/schedule/:token" element={<ScheduleInterviewPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          
          {/* Territory Routes (e.g., /union-somerset/careers) */}
          <Route path="/:territory/careers" element={<CareersPage />} />
          <Route path="/:territory/apply/:jobId" element={<ApplicationFlow />} />
          
          {/* Onboarding Route */}
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          } />
          
          {/* Protected Dashboard Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<DashboardHome />} />
            <Route path="candidates" element={<CandidatesPage />} />
            <Route path="candidates/:candidateId" element={<CandidateDetailPage />} />
            <Route path="employees" element={<EmployeesPage />} />
            <Route path="jobs" element={<JobListingsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="calendar" element={<InterviewCalendarPage />} />
          </Route>
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
